import { Check, CheckCheck, CircleAlert, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { deliveryLabel, failedText } from '@/lib/deliveryStatus';
import type { MessageDelivery } from '@/services/conversationService';

/** What the bubble sits on: the solid brand bubble (staff) or the light KI bubble. */
export type BubbleSurface = 'brand' | 'soft';

const ICONS = { accepted: Clock, sent: Check, delivered: CheckCheck, read: CheckCheck } as const;

/**
 * Colours per surface, for light and dark. `muted` = not read (yet); `read`
 * is the accent, in its own hue — a difference in opacity alone would not
 * carry. On the solid brand bubble (staff) that is green: minty on the
 * strong blue (light, 4.1:1), deep green on the light periwinkle of the dark
 * theme (3.6:1) — both above the 3:1 a graphic needs. On the light KI bubble
 * it is the brand blue (like WhatsApp's blue ticks).
 */
const TONES: Record<BubbleSurface, { muted: string; read: string }> = {
  brand: { muted: 'text-brand-foreground/75', read: 'text-emerald-200 dark:text-emerald-900' },
  soft: { muted: 'text-foreground/55', read: 'text-brand-strong' },
};

/**
 * The tick next to the time of a message we sent: clock (Meta took it), ✓
 * (sent), ✓✓ (delivered), ✓✓ in the accent (read). Named for screen readers;
 * the ticks changing is not announced (they are attributes, not new text).
 * A failed message gets a red dot here; the words are in `DeliveryFailure`.
 */
export function DeliveryMark({ delivery, surface }: { delivery: MessageDelivery; surface: BubbleSurface }) {
  if (delivery.status === 'failed') {
    return (
      <span
        aria-hidden="true"
        data-status="failed"
        className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full bg-destructive text-[10px] font-bold leading-none text-destructive-foreground"
      >
        !
      </span>
    );
  }
  const Icon = ICONS[delivery.status];
  const label = deliveryLabel(delivery);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-status={delivery.status}
      className={cn(
        'inline-flex flex-shrink-0 transition-colors',
        delivery.status === 'read' ? TONES[surface].read : TONES[surface].muted,
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
}

/**
 * "Nicht zugestellt · Grund" under a failed bubble — red on the page, which
 * reads in both themes (the bubble itself may be blue). Real text inside the
 * chat log, so it is announced like any new line; the red dot next to the
 * time is only its visual twin.
 */
export function DeliveryFailure({ delivery }: { delivery: MessageDelivery }) {
  if (delivery.status !== 'failed') return null;
  return (
    <div
      data-delivery="failed"
      className="flex max-w-full items-start justify-end gap-1 px-1 text-right text-[11.5px] font-medium leading-snug text-destructive-strong"
    >
      <CircleAlert className="mt-[1px] h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">{failedText(delivery)}</span>
    </div>
  );
}
